import { Exception } from '../models/Exception';
import { SecurityPolicyAuthorizer } from './SecurityPolicyAuthorizer';
/**
 * SecurityContext provides methods verifying policy actions. The intent is that an instance
 * is provided to every lambda handler.
 */
export class SecurityContext {
    constructor(principal, accessToken, policy) {
        this.principal = principal;
        this.accessToken = accessToken;
        this.policy = policy;
    }
    /**
     * Provided to support gatewayEventHandlerV2() in legacy lambda-based apis.
     */
    static fromJSON(json) {
        const data = JSON.parse(json);
        return new SecurityContext(data.principal, data.accessToken, data.policy);
    }
    /**
     * This should be invoked at the start of a handler that must be restricted to the user matching
     * the tenantId and accountId of the endpoint, in other words, the user who "owns" the endpoint.
     */
    requireSelf({ tenantId, accountId }) {
        if (!this.principal || this.principal.id !== accountId || this.principal.tenantId !== tenantId) {
            throw new Exception('Forbidden', 403);
        }
    }
    /**
     * This should be invoked at the start of a handler that must be restricted to users with a specific
     * policy rule. It will throw a "403" exception if the authenticated user does not meet this requirement.
     *
     * While an endpoint should only be protected by one policy, we occasionally need to evolve the policy needing to support both the old
     * policy and the new one. You can pass in additional ISecurityRequests and if any match, then requireAuthorizedTo will not throw.
     *
     * Example:
     *  securityContext.requireAuthorizedTo(
     *  { action: 'some-action', resource: '/some/resource' },
     *  { action: 'some-action-v2', resource: '/some/new/resource/pattern' },
     * )
     *
     * This allows us to evolve the schema without breaking consumers. IOW - a user could have the old policy
     *  { action: 'some-action', resource: '/some/*' }
     * And a user could have the new policy
     * { action: 'some-action-v2', resource: '/some/{my-tenant}/resource/123' }
     * And we can release supporting both until upgrading is a priority which often involves other teams / applications.
     */
    requireAuthorizedTo(request, ...deprecatedRequests) {
        if (this.policy) {
            const authorizer = this.getAuthorizer();
            if (authorizer.isAuthorizedTo(request)) {
                return;
            }
            for (const deprecatedRequest of deprecatedRequests) {
                if (authorizer.isAuthorizedTo(deprecatedRequest)) {
                    console.warn(`WARNING: User with accountId ${this.principal.id} was authorized using deprecated action ${deprecatedRequest.action}!`);
                    return;
                }
            }
        }
        throw new Exception('Principal is not authorized', 403, undefined, `${this.buildAuthFailureMessage(request)}.`);
    }
    /**
     * Provides access to underlying authorizer, as an alternative to invoking `requireAuthorzedTo()`. This is useful when a boolean result
     * is more convenient than an exception being thrown, for example when filtering data.
     */
    getAuthorizer() {
        return new SecurityPolicyAuthorizer(this.policy);
    }
    /**
     * Provides a string to be used for server-side logging. Should identify the authorized user without revealing secure or confidential data.
     */
    toLogSafeString() {
        if (!this.principal) {
            return 'anonymous';
        }
        const { id, username, email } = this.principal;
        return `${id}, ${username}, ${email}`;
    }
    /**
     * Helper for building error messages. Used internally, but may also be useful to subclasses.
     */
    buildAuthFailureMessage(request) {
        let description = `Policy does not include a rule for action "${request.action}"`;
        if (request.resource) {
            description += ` on resource "${request.resource}"`;
        }
        if (request.params) {
            description += ` with params ${JSON.stringify(request.params)}`;
        }
        return description;
    }
}
//# sourceMappingURL=SecurityContext.js.map


// WEBPACK FOOTER //
// ../../src/authorization/SecurityContext.ts